import * as React from 'react';
import { Dispatch, useMemo, useReducer, CSSProperties, ReducerAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Range, Direction, getTrackBackground } from 'react-range';
import Header from './components/header';
import Button from './components/button';
import { Colors } from './components/button/colors';
import { Steps } from './constants/common';
import { AppAction, AppState } from './constants/app.interface';
import trophyImage from './assets/trophy.svg';
import sadImage from './assets/sad.svg';
import './app.scss';
import { AnswerInterface } from './constants/common.interface';

const initialState: AppState = {
    isQuizStarted: false,
    isBetSelection: true,
    isWon: false,
    isLost: false,
    question: '',
    currentStepIndex: 0,
    currentStep: Steps[0],
    betSum: 1,
    bankroll: 10,
    betRangeValues: [1],
    isAnswered: false,
    answerIndex: 0,
};

type ThunkAction<T> = (dispatch: Dispatch<T>) => T
type AsyncDispatch<T> = Dispatch<T | ThunkAction<T>>

function wrapAsync<T>(dispatch: Dispatch<T>): AsyncDispatch<T> {
    return function (action: T | ThunkAction<any>) {
        if (action instanceof Function) {
            return action(dispatch)
        }
        return dispatch(action)
    }
}

function appReducer(state: AppState, action: AppAction) {
    switch (action.type) {
        case 'resetGame':
            return {...initialState};
        case 'startGame':
            return {...state, isQuizStarted: true};
        case 'nextStep':
            if (!state.isWon && !state.isLost) {
                return {
                    ...state,
                    currentStepIndex: state.currentStepIndex + 1,
                    currentStep: Steps[state.currentStepIndex + 1],
                    betSum: 1,
                    betRangeValues: [1],
                    isBetSelection: true,
                    isAnswered: false,
                    answerIndex: null,
                };
            }
            return state;
        case 'placeBet':
            return {
                ...state,
                isBetSelection: false,
            };
        case 'setBetRangeValues':
            return {
                ...state,
                betRangeValues: action.payload,
                betSum: action.payload[0],
            };
        case 'wonGame':
            return {
                ...state,
                isWon: true,
            };
        case 'lostGame':
            return {
                ...state,
                isLost: true,
            };
        case 'makeAnswer':
            const bankroll: number = action.payload.bankroll;

            return {
                ...state,
                bankroll: bankroll,
                isAnswered: true,
                answerIndex: action.payload.answerIndex,
            };
        default:
            throw new Error();
    }
}

function App() {
    const { t } = useTranslation('translations');
    const [state, dispatch]: [AppState, any] = useReducer(appReducer, initialState);
    const asyncDispatch: Dispatch<ReducerAction<any>> = useMemo(() => wrapAsync(dispatch), [dispatch]);

    const getStepProgressFilledStyles = (): CSSProperties => {
        return {
            width: `${(100 / 15) * (state.currentStepIndex + 1)}%`,
        }
    };

    const getBankrollMods = (): string => {
        const classList: string[] = ['step__bankroll'];
        if (state.isWon) classList.push('step__bankroll_won');
        if (state.isLost) classList.push('step__bankroll_lost');
        if (!state.isWon && !state.isLost && !state.isBetSelection) classList.push('step__bankroll_right');
        return classList.join(' ');
    };

    const getFooterMods = (): string => {
        const classList: string[] = ['step__footer'];
        if (!state.isWon && !state.isLost && !state.isBetSelection) classList.push('step__footer_revert');
        return classList.join(' ');
    };

    const getAnswerMods = (isCorrect: boolean, index: number): string => {
        const classList: string[] = ['step__answer-btn'];
        if (state.isAnswered) {
            if (isCorrect) {
                classList.push('step__answer-btn_correct');
            } else if (index === state.answerIndex) {
                classList.push('step__answer-btn_incorrect');
            }
        }
        return classList.join(' ');
    };

    const betChangeHandler = (values: number[]): void => {
        if (values[0] === 0) values[0] = 1;
        dispatch({type: 'setBetRangeValues', payload: values});
    };

    const answerHandler = (answer: AnswerInterface, index: number): void => {
        if (!state.isAnswered) {
            const bankroll: number = answer.isCorrect
                ? state.bankroll + state.betSum
                : state.bankroll - state.betSum;

            dispatch({type: 'makeAnswer', payload: {bankroll, answerIndex: index}});
            setTimeout(() => {
                if (bankroll) {
                    if (state.currentStepIndex + 1 === Steps.length) {
                        asyncDispatch({type: 'wonGame'});
                    } else {
                        asyncDispatch({type: 'nextStep'});
                    }
                } else {
                    asyncDispatch({type: 'lostGame'});
                }
            }, 2000);
        }
    };

    const restartHandler = (): void => {
        if (!state.isAnswered) {
            dispatch({type: 'resetGame'})
        }
    };

    const getEndText = () => {
        if (state.isWon) {
            if (state.bankroll < 101) {
                return t('ending.endTextSmallAmount')
            } else if (state.bankroll < 200001) {
                return t('ending.endTextMidAmount')
            }
            return t('ending.wonText')
        }
        return t('ending.lostText')
    };

    return (
        <div className="app__wrapper">
            <Header/>
            <div className="app__body">
                {!state.isQuizStarted && (
                    <React.Fragment>
                        <div className="app__body-text">
                            {t('introduction.firstParagraph')}
                            <br/>
                            <br/>
                            {t('introduction.secondParagraph')}
                        </div>
                        <div className='app__body-start-btn'>
                            <Button
                                click={() => dispatch({type: 'startGame'})}
                                text={t('introduction.startButton')}
                            />
                        </div>
                    </React.Fragment>
                )}
                {state.isQuizStarted && (
                    <React.Fragment>
                        {(!state.isWon && !state.isLost) && (
                            <React.Fragment>
                                <div className='step__progress'>
                                    <div className='step__current-number'>
                                        {`${t('quiz.step')} ${state.currentStepIndex + 1}`}
                                    </div>
                                    <div className='step__progress-bar'>
                                        <div
                                            className='step__progress-filled'
                                            style={getStepProgressFilledStyles()}
                                        />
                                    </div>
                                    <div className='step__count'>
                                        {`${t('quiz.step')} ${Steps.length}`}
                                    </div>
                                </div>
                                <div className='step__question'>
                                    {t(`questions.${state.currentStep.step - 1}.question`)}
                                </div>
                            </React.Fragment>
                        )}

                        {(state.isWon || state.isLost) && (
                            <React.Fragment>
                                <div className='step__end-icon'>
                                    <img src={state.isWon ? trophyImage : sadImage} alt={t('ending.iconAlternateText')}/>
                                </div>
                                <div className='step__end-title'>
                                    {state.isWon ? t('ending.wonTitle') : t('ending.lostTitle')}
                                </div>
                                <div className='step__end-text'>
                                    {getEndText()}
                                </div>
                            </React.Fragment>
                        )}
                        {state.isBetSelection && (
                            <div className='bet'>
                                <div className='bet__chosen-sum'>
                                    {`${t('quiz.bet')} $${state.betSum}`}
                                </div>
                                <div className='bet__range'>
                                    <Range
                                        values={state.betRangeValues}
                                        min={state.bankroll === 1 ? 0 : 1}
                                        max={state.bankroll}
                                        step={1}
                                        direction={Direction.Right}
                                        allowOverlap={false}
                                        draggableTrack={false}
                                        disabled={false}
                                        rtl={false}
                                        onChange={betChangeHandler}
                                        renderThumb={({props}) => (
                                            <div
                                                {...props}
                                                className='bet__range-thumb'
                                            />
                                        )}
                                        renderTrack={({props, children}) => (
                                            <div
                                                {...props}
                                                className='bet__range-track'
                                                style={{
                                                    background: getTrackBackground({
                                                        values: state.betRangeValues,
                                                        colors: [Colors.green, Colors.lightGrey],
                                                        min: state.bankroll === 1 ? 0 : 1,
                                                        max: state.bankroll,
                                                    }),
                                                }}
                                            >
                                                {children}
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                        {(!state.isWon && !state.isLost && !state.isBetSelection) && (
                            <div className='step__answer-list'>
                                {state.currentStep.answerOptions.map((answer, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className={getAnswerMods(!!answer.isCorrect, index)}
                                            onClick={() => answerHandler(answer, index)}
                                        >
                                            {t(`questions.${state.currentStep.step - 1}.answers.${index}`)}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                        <div className={getFooterMods()}>
                            <div className={getBankrollMods()}>
                                <svg className='step__bankroll-icon' width="18" height="18" viewBox="0 0 18 18"
                                     fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M10.77 4.61475C10.6993 4.54141 10.6019 4.5 10.5 4.5H7.50004C7.39819 4.5 7.30073 4.54141 7.23004 4.61475C6.68177 5.18249 1.875 10.2255 1.875 12.375C1.875 15.477 5.07151 18 9.00001 18C12.9285 18 16.125 15.477 16.125 12.375C16.125 10.2255 11.3182 5.18249 10.77 4.61475Z"
                                        fill="white"/>
                                    <path
                                        d="M13.7812 0.50175C13.1565 -0.20475 11.5312 0.72225 10.5982 1.35C10.1707 0.7425 9.53774 0 8.99999 0C8.46224 0 7.82925 0.7425 7.40175 1.35C6.47099 0.721512 4.84273 -0.205488 4.21874 0.50175C4.04577 0.701191 3.96417 0.963914 3.99374 1.22625C4.1145 2.61524 6.94198 4.91101 7.26373 5.16751C7.3307 5.22127 7.41409 5.25037 7.49998 5.25002H10.5C10.585 5.25009 10.6675 5.22127 10.734 5.16829C11.0557 4.91105 13.884 2.6183 14.004 1.22702C14.0347 0.964723 13.9539 0.701578 13.7812 0.50175Z"
                                        fill="white"/>
                                    <path
                                        d="M12 5.24999H6.00001C5.79291 5.24999 5.625 5.08208 5.625 4.87498C5.625 4.66787 5.79291 4.5 6.00001 4.5H12C12.2071 4.5 12.375 4.66791 12.375 4.87501C12.375 5.08212 12.2071 5.24999 12 5.24999Z"
                                        fill="#388E3C"/>
                                    <path
                                        d="M8.99999 15.0002C8.10825 15.0883 7.296 14.4833 7.125 13.6037C7.1076 13.3966 7.26141 13.2146 7.46851 13.1972C7.67562 13.1798 7.85762 13.3336 7.87502 13.5407C7.90803 13.9322 8.41277 14.2502 9.00002 14.2502C9.61051 14.2502 10.125 13.9067 10.125 13.5002C10.125 13.1342 9.76952 12.9002 9.00677 12.7577L8.92503 12.7427C7.70778 12.5177 7.12503 12.0205 7.12503 11.2502C7.24235 10.3259 8.0725 9.66178 9.00002 9.7502C9.89176 9.66214 10.704 10.2671 10.875 11.1467C10.8924 11.3538 10.7386 11.5358 10.5315 11.5532C10.3244 11.5706 10.1424 11.4168 10.125 11.2097C10.0935 10.8182 9.58875 10.5002 8.99999 10.5002C8.3895 10.5002 7.87499 10.8437 7.87499 11.2502C7.87499 11.6162 8.23123 11.8502 8.99475 11.9927L9.07726 12.0085C10.2923 12.2335 10.8773 12.73 10.8773 13.501C10.7595 14.4259 9.92811 15.09 8.99999 15.0002Z"
                                        fill="#303030"/>
                                    <path
                                        d="M9.00001 15.75C8.79291 15.75 8.625 15.5821 8.625 15.375V9.37501C8.625 9.16791 8.79291 9 9.00001 9C9.20712 9 9.37502 9.16791 9.37502 9.37501V15.375C9.37502 15.5821 9.20712 15.75 9.00001 15.75Z"
                                        fill="#303030"/>
                                </svg>
                                <div>
                                {`${t('quiz.bankroll')} $${state.bankroll}`}
                                </div>
                            </div>
                            {state.isBetSelection && (
                                <Button
                                    text={t('quiz.betButton')}
                                    backgroundColor={Colors.blue}
                                    click={() => dispatch({type: 'placeBet'})}
                                />
                            )}
                            {(!state.isWon && !state.isLost && !state.isBetSelection) && (
                                <Button
                                    text={t('quiz.restartButton')}
                                    backgroundColor={Colors.lightGrey}
                                    click={restartHandler}
                                />
                            )}
                            {(state.isWon || state.isLost) && (
                                <Button
                                    text={t('ending.againButton')}
                                    backgroundColor={Colors.blue}
                                    click={() => dispatch({type: 'resetGame'})}
                                />
                            )}
                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    );
}

export default App;
